import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import listedProAdminService from '../../../services/listedProAdminService';
import partyEngineService from '../../../services/partyEngineService';
import { proService } from '../../../services';
import { useDialog } from '../../../context/DialogContext';
import { useUser } from '../../../context/UserContext';
import { Btn, ProgressBar, PartyEngineStyles, Chevron, fmtWhen } from '../partyEngine/ui';
import { LISTED_GRAY, MUTED } from '../../pro/proUi';
import { useAdminShell } from '../shell/AdminShellContext';

// The statuses the API actually sends are queued / researching / completed /
// failed / canceled (ListedProRun). There is no "running".
const ACTIVE = new Set(['queued', 'researching']);
// "researching" is resumable too: a run whose process died never wrote a terminal
// status, so it sits mid-flight forever with nothing driving it.
const RESUMABLE = new Set(['failed', 'canceled', 'researching']);
const money = (usd) => `$${Number(usd || 0).toFixed(2)}`;

/* ── chips ── */
function Chip({ children, bg = '#fff', color = '#000', border = '#000', className = '' }) {
  return (
    <span className={`inline-block whitespace-nowrap border-2 font-mono font-bold uppercase ${className}`} style={{ fontSize: 10, letterSpacing: '0.05em', padding: '3px 7px', background: bg, color, borderColor: border }}>{children}</span>
  );
}
function StatusPill({ status }) {
  const map = { researching: ['#F9B82C', '#000'], queued: ['#fff', '#000'], completed: ['#15803D', '#fff'], failed: ['#DC2626', '#fff'], canceled: ['#fff', MUTED] };
  const [bg, color] = map[status] || ['#fff', '#000'];
  const label = status === 'completed' ? 'DONE' : status.toUpperCase();
  return <Chip bg={bg} color={color}>{label}</Chip>;
}
/* Contact / verdict are black-outline words — the accents are reserved for notices and newsletter. */
function ContactChip({ source }) {
  const map = { own_domain: 'OWN DOMAIN', own_site: 'OWN SITE', social: 'SOCIAL', other: 'OTHER' };
  return <Chip>{map[source] || map.other}</Chip>;
}
function VerdictChip({ verdict }) {
  const map = { created: 'CREATED', duplicate: 'DUPLICATE', rejected: 'REJECTED', unverifiable: 'UNVERIFIABLE', pending: 'PENDING' };
  return <Chip>{map[verdict] || String(verdict || '').toUpperCase()}</Chip>;
}
function Header({ children, right }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#F2F2F2]" style={{ padding: '10px 14px' }}>
      <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em' }}>{children}</span>
      {right}
    </div>
  );
}

/* ── health ── */
function HealthStrip({ health }) {
  if (!health) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <Chip bg={health.enabled ? '#15803D' : '#DC2626'} color="#fff" className="!px-3 !py-2 !text-[11px]">{health.enabled ? 'LISTED PROS ON' : 'LISTED PROS OFF — ENABLE LISTED_PROS_ENABLED IN SETTINGS'}</Chip>
      <Chip bg={health.llmConfigured ? '#15803D' : '#DC2626'} color="#fff" className="!px-3 !py-2 !text-[11px]">{health.llmConfigured ? 'KEYS OK' : 'ANTHROPIC KEY MISSING'}</Chip>
      <Chip bg={health.activeRuns > 0 ? '#F9B82C' : '#fff'} color="#000" className="!px-3 !py-2 !text-[11px]">{health.activeRuns} RUN{health.activeRuns === 1 ? '' : 'S'} ACTIVE</Chip>
      <Chip className="!px-3 !py-2 !text-[11px]">{health.listed} LISTED · {health.claimed} CLAIMED</Chip>
      {!health.emailConfigured && <Chip bg="#F9B82C" className="!px-3 !py-2 !text-[11px]">EMAIL OFF — INVITES LOGGED ONLY</Chip>}
    </div>
  );
}

/* ── start a run ── */
function StartRun({ health, onStarted }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [region, setRegion] = useState(null);
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { proService.getCraftGroups().then((r) => { if (r.success && r.data?.groups) setGroups(r.data.groups); }); }, []);
  useEffect(() => {
    if (!query.trim() || region) { setResults([]); return undefined; }
    const t = setTimeout(async () => { const r = await partyEngineService.searchRegions(query.trim()); if (r.success) setResults(r.data || []); }, 250);
    return () => clearTimeout(t);
  }, [query, region]);

  const toggle = (slug) => setPicked((p) => (p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]));
  const toggleGroup = (g) => setPicked((p) => {
    const slugs = g.crafts.map((c) => c.slug);
    const all = slugs.every((s) => p.includes(s));
    return all ? p.filter((x) => !slugs.includes(x)) : Array.from(new Set([...p, ...slugs]));
  });
  const disabled = !health?.enabled || !region || picked.length === 0 || busy;

  const start = async () => {
    setBusy(true); setError('');
    const r = await listedProAdminService.startRun(region.id, picked);
    setBusy(false);
    if (!r.success) { setError((r.error || 'Could not start.').toUpperCase()); return; }
    setPicked([]); setRegion(null); setQuery('');
    onStarted?.(r.data);
  };

  return (
    <div className="overflow-hidden rounded-[24px] bg-white">
      <Header>START A RUN</Header>
      <div className="flex flex-col gap-4" style={{ padding: 14 }}>
        <div className="relative" style={{ maxWidth: 320 }}>
          <label className="mb-1 block font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }}>REGION</label>
          {region ? (
            <div className="flex items-center justify-between rounded-full bg-[#F2F2F2] px-4 font-mono" style={{ height: 40, fontSize: 12 }}>
              <span className="truncate font-bold uppercase">{region.name}</span>
              <button type="button" onClick={() => setRegion(null)} className="font-bold uppercase underline" style={{ fontSize: 10 }}>CHANGE</button>
            </div>
          ) : (
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="TYPE A CITY" aria-label="Region"
              className="w-full rounded-full border-2 border-transparent bg-[#F2F2F2] px-4 font-mono uppercase outline-none focus:border-black focus:bg-white" style={{ height: 40, fontSize: 12, letterSpacing: '0.05em' }} />
          )}
          {!region && results.length > 0 && (
            <div className="absolute left-0 right-0 z-10 overflow-hidden rounded-[20px] bg-white" style={{ boxShadow: '0 12px 32px rgba(0,0,0,.14)' }}>
              {results.map((r, i) => (
                <button key={r.id} type="button" onClick={() => { setRegion(r); setResults([]); }}
                  className="block w-full px-3 py-2 text-left font-mono font-bold uppercase hover:bg-gray-50" style={{ fontSize: 11, borderTop: i === 0 ? 'none' : '2px solid #000' }}>
                  {r.name}{r.hasActiveRun ? <span style={{ color: MUTED }}> · RUN ACTIVE</span> : ''}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }}>CRAFTS</span>
            <span className="font-mono font-bold uppercase" style={{ fontSize: 10, color: MUTED }}>{picked.length} PICKED</span>
          </div>
          <div className="flex flex-col gap-2">
            {groups.map((g) => (
              <div key={g.key} className="flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={() => toggleGroup(g)} className="font-mono font-bold uppercase underline hover:opacity-80" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED, minWidth: 110, textAlign: 'left' }}>{g.label}</button>
                {g.crafts.map((c) => {
                  const on = picked.includes(c.slug);
                  return (
                    <button key={c.slug} type="button" onClick={() => toggle(c.slug)} aria-pressed={on}
                      className={`rounded-full font-mono font-bold uppercase transition-colors ${on ? 'bg-black text-white' : 'bg-[#F2F2F2] hover:bg-[#E5E5E5]'}`} style={{ fontSize: 9, letterSpacing: '0.12em', padding: '6px 12px' }}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {error && <div className="font-mono font-bold uppercase" style={{ fontSize: 11, color: '#DC2626' }} role="alert">{error}</div>}
        <div className="flex items-center gap-3">
          <Btn variant="primary" onClick={start} disabled={disabled}>{busy ? 'STARTING…' : 'RUN'}</Btn>
          {!health?.enabled && <span className="font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>DISABLED — LISTED PROS OFF</span>}
          {health?.enabled && !region && <span className="font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>PICK A REGION.</span>}
          {health?.enabled && region && picked.length === 0 && <span className="font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>PICK AT LEAST ONE CRAFT.</span>}
        </div>
      </div>
    </div>
  );
}

/* ── runs ── */
function RunRow({ run, onView, active = false }) {
  return (
    <div className="flex flex-wrap items-center gap-4 border-t border-[#F2F2F2] first:border-t-0" style={{ padding: '12px 14px', background: active ? '#F5F5F5' : undefined }} data-testid="listed-run" aria-current={active ? 'true' : undefined}>
      <div className="min-w-0 flex-1" style={{ minWidth: 160 }}>
        <div className="truncate font-mono font-bold uppercase" style={{ fontSize: 12 }}>{run.regionName}</div>
        <div className="font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>{run.crafts.length} CRAFT{run.crafts.length === 1 ? '' : 'S'} · {fmtWhen(run.createdAt)}{run.requestedByHandle ? ` · @${run.requestedByHandle}` : ''}</div>
      </div>
      <StatusPill status={run.status} />
      <div style={{ width: 140 }}>
        <ProgressBar percent={run.progress} height={10} pulse={ACTIVE.has(run.status)} />
        <div className="mt-1 font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>{run.progress}%{run.stageLabel ? ` · ${run.stageLabel}` : ''}</div>
      </div>
      <div className="font-mono uppercase" style={{ fontSize: 10 }}>
        {run.businessesFound} FOUND · {run.businessesCreated} CREATED · {run.businessesDuplicate} DUPLICATES · {run.businessesRejected} REJECTED
      </div>
      <div className="font-mono font-bold" style={{ fontSize: 11 }}>{money(run.estimatedCostUsd)}</div>
      <button type="button" onClick={onView} className="font-mono font-bold uppercase hover:underline" style={{ fontSize: 11 }}>VIEW →</button>
      {run.lastError && <div className="w-full font-mono" style={{ fontSize: 10, color: '#DC2626' }}>{run.lastError}</div>}
    </div>
  );
}

function RunDetail({ runId, onBack, onChanged, canEdit }) {
  const [run, setRun] = useState(null);
  const { showConfirm } = useDialog();
  const load = useCallback(async () => { const r = await listedProAdminService.getRun(runId); if (r.success) setRun(r.data); }, [runId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!run || !ACTIVE.has(run.status)) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [run, load]);
  const cancel = async () => {
    const ok = await showConfirm({ title: 'CANCEL RUN', message: 'Stop this run? Businesses already created stay.', confirmLabel: 'CANCEL RUN', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    await listedProAdminService.cancelRun(runId); load(); onChanged?.();
  };
  // What's left to research. No button when there's nothing to do — the backend
  // refuses that case anyway.
  const remaining = (run?.crafts?.length || 0) - (run?.craftsDone?.length || 0);
  // Stopped partway — dropped connection, a restart mid-deploy, retries spent, or
  // canceled by hand. The crafts already researched are banked, so this picks up at
  // the next one rather than paying for them again.
  const resume = async () => {
    const r = await listedProAdminService.resumeRun(runId);
    if (!r.success) {
      await showConfirm({ title: 'CANNOT RESUME', message: r.error || 'Could not resume that run.', confirmLabel: 'OK', cancelLabel: 'CLOSE', theme: 'admin' });
      return;
    }
    load(); onChanged?.();
  };
  if (!run) return <div className="rounded-[24px] bg-white p-6 font-mono text-xs uppercase" style={{ color: MUTED }}>LOADING…</div>;
  return (
    <div className="overflow-hidden rounded-[24px] bg-white">
      <Header right={(
        <div className="flex items-center gap-2">
          <StatusPill status={run.status} />
          {canEdit && ACTIVE.has(run.status) && <Btn size="sm" variant="danger" onClick={cancel}>CANCEL</Btn>}
          {/* Offered wherever there is unfinished work: a stopped run, and also one
              stuck in "running" because the process died without writing an end state. */}
          {canEdit && RESUMABLE.has(run.status) && remaining > 0 && (
            <Btn size="sm" onClick={resume}>RESUME</Btn>
          )}
          <Btn size="sm" onClick={onBack}>← RUNS</Btn>
        </div>
      )}>
        RUN · {run.regionName}
      </Header>
      <div className="flex flex-wrap items-center gap-4 border-b border-[#F2F2F2] font-mono uppercase" style={{ padding: '10px 14px', fontSize: 10 }}>
        <span>{run.crafts.join(' · ')}</span>
        <span style={{ color: MUTED }}>{run.businessesFound} FOUND · {run.businessesCreated} CREATED · {run.businessesDuplicate} DUPLICATES · {run.businessesRejected} REJECTED</span>
        <span className="font-bold">{money(run.estimatedCostUsd)}</span>
        <span style={{ color: MUTED }}>{run.searches} SEARCHES · {run.llmCalls} LLM CALLS</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full font-mono" style={{ fontSize: 11 }}>
          <thead>
            <tr className="border-b border-[#F2F2F2] text-left uppercase" style={{ fontSize: 10, letterSpacing: '0.1em' }}>
              {['NAME', 'CRAFT', 'WEBSITE', 'CONTACT', 'VERDICT', ''].map((h) => <th key={h} className="font-bold" style={{ padding: '8px 14px' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {run.candidates.length === 0 && <tr><td colSpan={6} style={{ padding: 14, color: MUTED }}>NO CANDIDATES YET.</td></tr>}
            {run.candidates.map((c) => (
              <tr key={c.id} className="border-t border-[#F2F2F2]" data-testid="candidate-row">
                <td style={{ padding: '8px 14px' }}>
                  <div className="font-bold">{c.name}</div>
                  {c.reason && <div style={{ fontSize: 10, color: MUTED }}>{c.reason}</div>}
                </td>
                <td className="uppercase" style={{ padding: '8px 14px' }}>{c.craft}</td>
                <td style={{ padding: '8px 14px' }}>{c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="underline">{c.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a> : <span style={{ color: LISTED_GRAY }}>—</span>}</td>
                <td style={{ padding: '8px 14px' }}><ContactChip source={c.contactSource} /></td>
                <td style={{ padding: '8px 14px' }}><VerdictChip verdict={c.verdict} /></td>
                <td style={{ padding: '8px 14px' }}>
                  {c.businessSlug
                    ? <a href={`/pro/${c.businessSlug}`} target="_blank" rel="noreferrer" className="font-bold uppercase hover:underline">VIEW</a>
                    : <span className="font-bold uppercase" style={{ color: LISTED_GRAY }}>VIEW</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── businesses / claims / removals ── */
function BusinessRow({ b, actions }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-[#F2F2F2] first:border-t-0" style={{ padding: '10px 14px' }} data-testid="listed-business">
      <div className="min-w-0 flex-1" style={{ minWidth: 180 }}>
        <div className="flex items-center gap-2">
          <a href={`/pro/${b.slug}`} target="_blank" rel="noreferrer" className="truncate font-mono font-bold hover:underline" style={{ fontSize: 13 }}>{b.name}</a>
          {b.isListed ? <Chip bg={LISTED_GRAY}>LISTED</Chip> : <Chip>CLAIMED</Chip>}
          {!b.isPublished && <Chip>UNPUBLISHED</Chip>}
        </div>
        <div className="font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>
          {[b.city, b.crafts.join(' · '), `${b.openRequests} REQUEST${b.openRequests === 1 ? '' : 'S'}`, b.lastOutreachAt ? `INVITED ${fmtWhen(b.lastOutreachAt)}` : 'NOT INVITED', b.claimedByHandle ? `CLAIMED BY @${b.claimedByHandle}` : null].filter(Boolean).join(' · ')}
        </div>
      </div>
      <ContactChip source={b.contactSource} />
      <div className="flex flex-none items-center gap-1.5">{actions}</div>
    </div>
  );
}

/* ── panel ── */
/**
 * ENGINES › LISTED PROS — spec/sections.md §19. Gates (`listed_pros`), absent not disabled:
 * create — START A RUN; edit — run CANCEL, RESEND INVITE; publish — claim APPROVE / REJECT,
 * removal REMOVE / KEEP, business PUBLISH / UNPUBLISH / REMOVE. Run detail is the nested
 * route `/admin/listed-pros/runs/:runId`; old `?tab=listedpros&run=` links are redirected.
 */
export default function ListedProsPanel() {
  const navigate = useNavigate();
  const { runId: viewRun } = useParams();
  const [params] = useSearchParams();
  const { showConfirm } = useDialog();
  const { can } = useUser();
  const { refreshCounts } = useAdminShell();
  const canCreate = can('listed_pros', 'create');
  const canEdit = can('listed_pros', 'edit');
  const canPublish = can('listed_pros', 'publish');
  const [health, setHealth] = useState(null);
  const [runs, setRuns] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [filter, setFilter] = useState('all');
  const detailRef = useRef(null);

  const legacyRun = params.get('run');
  useEffect(() => {
    if (legacyRun && !viewRun) navigate(`/admin/listed-pros/runs/${legacyRun}`, { replace: true });
  }, [legacyRun, viewRun, navigate]);
  useEffect(() => {
    if (viewRun && detailRef.current) detailRef.current.scrollIntoView?.({ block: 'start' });
  }, [viewRun]);

  const load = useCallback(async () => {
    const [h, r, b] = await Promise.all([listedProAdminService.health(), listedProAdminService.listRuns({ take: 30 }), listedProAdminService.businesses({ take: 300 })]);
    if (h.success) setHealth(h.data);
    if (r.success) setRuns(r.data || []);
    if (b.success) setBusinesses(b.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!runs.some((r) => ACTIVE.has(r.status))) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [runs, load]);

  const setRun = (id) => navigate(id ? `/admin/listed-pros/runs/${id}` : '/admin/listed-pros');

  const act = async (b, action, confirm) => {
    if (confirm) { const ok = await showConfirm({ ...confirm, cancelLabel: 'BACK', theme: 'admin' }); if (!ok) return; }
    const r = await listedProAdminService.action(b.id, action);
    if (!r.success) await showConfirm({ title: 'FAILED', message: r.error || 'Action failed.', confirmLabel: 'OK', cancelLabel: 'CLOSE', theme: 'admin' });
    load();
    refreshCounts();
  };

  const claims = useMemo(() => businesses.filter((b) => b.claimPending), [businesses]);
  const removals = useMemo(() => businesses.filter((b) => b.removalRequestedAt), [businesses]);
  const shown = useMemo(() => businesses.filter((b) => filter === 'all' || (filter === 'listed' ? b.isListed : filter === 'claimed' ? !b.isListed : !b.isPublished)), [businesses, filter]);

  return (
    <div className="flex flex-col gap-4 font-mono">
      <PartyEngineStyles />
      <HealthStrip health={health} />
      {canCreate && <StartRun health={health} onStarted={(r) => { load(); if (r?.id) setRun(r.id); }} />}

      <div className="overflow-hidden rounded-[24px] bg-white">
        <Header>RUNS</Header>
        {runs.length === 0 ? <div style={{ padding: 14, fontSize: 12, color: MUTED }}>{canCreate ? 'NO RUNS YET. START ONE ABOVE.' : 'NO RUNS YET.'}</div> : runs.map((r) => <RunRow key={r.id} run={r} active={r.id === viewRun} onView={() => setRun(r.id)} />)}
      </div>
      {viewRun && (
        <div ref={detailRef} style={{ scrollMarginTop: 16 }}>
          <RunDetail key={viewRun} runId={viewRun} onBack={() => setRun(null)} onChanged={load} canEdit={canEdit} />
        </div>
      )}

      {claims.length > 0 && (
        <div className="overflow-hidden rounded-[24px] bg-white" data-testid="claims-panel">
          <Header right={<Chip bg="#F9B82C">{claims.length} WAITING</Chip>}>CLAIMS TO APPROVE</Header>
          {claims.map((b) => (
            <BusinessRow key={b.id} b={b} actions={(
              <>
                <span className="mr-2 whitespace-nowrap font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>@{b.claimRequestedByHandle || '?'} · {fmtWhen(b.claimRequestedAt)}{b.claimNeedsAdmin ? ' · NO PUBLIC CONTACT' : ''}</span>
                {canPublish && (
                  <>
                    <Btn size="sm" variant="primary" className="whitespace-nowrap" onClick={() => act(b, 'approve-claim', { title: 'APPROVE CLAIM', message: `Hand ${b.name} to @${b.claimRequestedByHandle}? Their stored requests become offers.`, confirmLabel: 'APPROVE', variant: 'primary' })}>APPROVE</Btn>
                    <Btn size="sm" className="whitespace-nowrap" onClick={() => act(b, 'reject-claim', { title: 'REJECT CLAIM', message: `Reject @${b.claimRequestedByHandle}'s claim on ${b.name}?`, confirmLabel: 'REJECT', variant: 'danger' })}>REJECT</Btn>
                  </>
                )}
              </>
            )} />
          ))}
        </div>
      )}

      {removals.length > 0 && (
        <div className="overflow-hidden rounded-[24px] bg-white" data-testid="removals-panel">
          <Header right={<Chip bg="#F9B82C">{removals.length} OPEN</Chip>}>REMOVAL REQUESTS</Header>
          {removals.map((b) => (
            <div key={b.id} className="border-t border-[#F2F2F2] first:border-t-0" style={{ padding: '10px 14px' }}>
              <div className="flex flex-wrap items-center gap-3">
                <a href={`/pro/${b.slug}`} target="_blank" rel="noreferrer" className="font-bold hover:underline" style={{ fontSize: 13 }}>{b.name}</a>
                <span className="uppercase" style={{ fontSize: 10, color: MUTED }}>{fmtWhen(b.removalRequestedAt)}</span>
                {canPublish && (
                  <div className="ml-auto flex gap-1.5">
                    <Btn size="sm" variant="dangerFilled" className="whitespace-nowrap" onClick={() => act(b, 'remove', { title: 'REMOVE LISTING', message: `Take ${b.name} down for good? Open requests are closed.`, confirmLabel: 'REMOVE', variant: 'danger' })}>REMOVE</Btn>
                    <Btn size="sm" className="whitespace-nowrap" onClick={() => act(b, 'publish')}>KEEP</Btn>
                  </div>
                )}
              </div>
              <p className="mt-1" style={{ fontSize: 12 }}>{b.removalReason || <span style={{ color: MUTED }}>No reason given.</span>}</p>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-[24px] bg-white">
        <Header right={(
          <div className="flex gap-1">
            {['all', 'listed', 'claimed', 'unpublished'].map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)} className={`rounded-full px-3 py-[6px] font-bold uppercase ${filter === f ? 'bg-black text-white' : 'bg-[#F2F2F2]'}`} style={{ fontSize: 9, letterSpacing: '0.12em' }}>{f}</button>
            ))}
          </div>
        )}>BUSINESSES · {shown.length}</Header>
        {shown.length === 0 ? <div style={{ padding: 14, fontSize: 12, color: MUTED }}>NOTHING HERE.</div> : shown.map((b) => (
          <BusinessRow key={b.id} b={b} actions={b.isListed ? (
            <>
              {canPublish && (b.isPublished
                ? <Btn size="sm" className="whitespace-nowrap" onClick={() => act(b, 'unpublish')}>UNPUBLISH</Btn>
                : <Btn size="sm" className="whitespace-nowrap" onClick={() => act(b, 'publish')}>PUBLISH</Btn>)}
              {canPublish && <Btn size="sm" variant="danger" className="whitespace-nowrap" onClick={() => act(b, 'remove', { title: 'REMOVE LISTING', message: `Remove ${b.name}? This can't be undone.`, confirmLabel: 'REMOVE', variant: 'danger' })}>REMOVE</Btn>}
              {canEdit && (b.hasEmail || b.hasPhone) && <Btn size="sm" className="whitespace-nowrap" onClick={() => act(b, 'resend-invite')}>RESEND INVITE</Btn>}
            </>
          ) : <span className="whitespace-nowrap uppercase" style={{ fontSize: 10, color: MUTED }}>CLAIMED {fmtWhen(b.claimedAt)}</span>} />
        ))}
      </div>
      <div className="flex items-center gap-1 font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>
        <Chevron open={false} /> SEE ALSO: SETTINGS → LISTED_PROS_ENABLED. CLAIM LINKS LAST SEVEN DAYS.
      </div>
    </div>
  );
}
